import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationStatus = 'not-started' | 'in-progress' | 'submitted';
export type AssessmentStatus = 'not-scheduled' | 'scheduled' | 'completed';
export type ChecklistItemStatus = 'pending' | 'done';

export interface ChecklistItem {
  id: string;
  label: string;
  status: ChecklistItemStatus;
  completedDate: string | null;
}

/**
 * A manually-logged attendance entry for off-app instruction
 * (field trips, physical curriculum, co-op classes, tutors, etc.)
 *
 * Merged with activity-log-derived attendance by the compliance engine.
 */
export type AttendanceEntryKind = 'instruction' | 'excused' | 'holiday';

export interface ManualAttendanceEntry {
  id: string;
  date: string;                    // YYYY-MM-DD
  kind: AttendanceEntryKind;       // instruction = counts; excused/holiday = informational
  minutes: number;                 // 0 for holiday/excused
  subjects: string[];              // Subject[] (stored as string for forward-compat)
  notes: string;                   // optional context for portfolio
}

export interface ChildComplianceState {
  childId: string;
  stateCode: string | null;

  // Notification tracking
  notificationStatus: NotificationStatus;
  notificationDate: string | null;

  // Assessment tracking
  assessmentStatus: AssessmentStatus;
  assessmentDate: string | null;
  assessmentNotes: string;

  // Custom checklist items per child
  checklist: ChecklistItem[];

  // Manual attendance log (for off-app learning)
  manualAttendance: ManualAttendanceEntry[];
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface ComplianceStoreState {
  childStates: Record<string, ChildComplianceState>;

  // Actions
  getChildCompliance: (childId: string) => ChildComplianceState;
  setChildState: (childId: string, stateCode: string | null) => void;
  setNotificationStatus: (childId: string, status: NotificationStatus, date?: string | null) => void;
  setAssessmentStatus: (childId: string, status: AssessmentStatus, date?: string | null) => void;
  setAssessmentNotes: (childId: string, notes: string) => void;
  addChecklistItem: (childId: string, label: string) => void;
  toggleChecklistItem: (childId: string, itemId: string) => void;
  removeChecklistItem: (childId: string, itemId: string) => void;
  addAttendanceEntry: (childId: string, entry: Omit<ManualAttendanceEntry, 'id'>) => void;
  updateAttendanceEntry: (childId: string, entryId: string, patch: Partial<Omit<ManualAttendanceEntry, 'id'>>) => void;
  removeAttendanceEntry: (childId: string, entryId: string) => void;
  resetChildCompliance: (childId: string) => void;
}

function defaultChildState(childId: string): ChildComplianceState {
  return {
    childId,
    stateCode: null,
    notificationStatus: 'not-started',
    notificationDate: null,
    assessmentStatus: 'not-scheduled',
    assessmentDate: null,
    assessmentNotes: '',
    checklist: [],
    manualAttendance: [],
  };
}

/** Migrate legacy persisted state that may not have manualAttendance set. */
function ensureShape(s: ChildComplianceState): ChildComplianceState {
  return {
    ...s,
    manualAttendance: s.manualAttendance ?? [],
    checklist: s.checklist ?? [],
  };
}

export const useComplianceStore = create<ComplianceStoreState>()(
  persist(
    (set, get) => ({
      childStates: {},

      getChildCompliance: (childId) => {
        const existing = get().childStates[childId];
        return existing ? ensureShape(existing) : defaultChildState(childId);
      },

      setChildState: (childId, stateCode) => {
        set(state => {
          const existing = state.childStates[childId] ?? defaultChildState(childId);
          return {
            childStates: {
              ...state.childStates,
              [childId]: { ...existing, stateCode },
            },
          };
        });
      },

      setNotificationStatus: (childId, status, date) => {
        set(state => {
          const existing = state.childStates[childId] ?? defaultChildState(childId);
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                notificationStatus: status,
                notificationDate: date !== undefined ? date : existing.notificationDate,
              },
            },
          };
        });
      },

      setAssessmentStatus: (childId, status, date) => {
        set(state => {
          const existing = state.childStates[childId] ?? defaultChildState(childId);
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                assessmentStatus: status,
                assessmentDate: date !== undefined ? date : existing.assessmentDate,
              },
            },
          };
        });
      },

      setAssessmentNotes: (childId, notes) => {
        set(state => {
          const existing = state.childStates[childId] ?? defaultChildState(childId);
          return {
            childStates: {
              ...state.childStates,
              [childId]: { ...existing, assessmentNotes: notes },
            },
          };
        });
      },

      addChecklistItem: (childId, label) => {
        set(state => {
          const existing = state.childStates[childId] ?? defaultChildState(childId);
          const newItem: ChecklistItem = {
            id: crypto.randomUUID(),
            label,
            status: 'pending',
            completedDate: null,
          };
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                checklist: [...existing.checklist, newItem],
              },
            },
          };
        });
      },

      toggleChecklistItem: (childId, itemId) => {
        set(state => {
          const existing = state.childStates[childId] ?? defaultChildState(childId);
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                checklist: existing.checklist.map(item =>
                  item.id === itemId
                    ? {
                        ...item,
                        status: item.status === 'done' ? 'pending' as const : 'done' as const,
                        completedDate: item.status === 'done' ? null : new Date().toISOString().split('T')[0],
                      }
                    : item
                ),
              },
            },
          };
        });
      },

      removeChecklistItem: (childId, itemId) => {
        set(state => {
          const existing = state.childStates[childId] ?? defaultChildState(childId);
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                checklist: existing.checklist.filter(item => item.id !== itemId),
              },
            },
          };
        });
      },

      addAttendanceEntry: (childId, entry) => {
        set(state => {
          const existing = ensureShape(state.childStates[childId] ?? defaultChildState(childId));
          const newEntry: ManualAttendanceEntry = {
            id: crypto.randomUUID(),
            ...entry,
          };
          // Replace any existing manual entry for the same date (one manual record per date)
          const filtered = existing.manualAttendance.filter(e => e.date !== entry.date);
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                manualAttendance: [...filtered, newEntry].sort((a, b) => a.date.localeCompare(b.date)),
              },
            },
          };
        });
      },

      updateAttendanceEntry: (childId, entryId, patch) => {
        set(state => {
          const existing = ensureShape(state.childStates[childId] ?? defaultChildState(childId));
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                manualAttendance: existing.manualAttendance.map(e =>
                  e.id === entryId ? { ...e, ...patch } : e
                ),
              },
            },
          };
        });
      },

      removeAttendanceEntry: (childId, entryId) => {
        set(state => {
          const existing = ensureShape(state.childStates[childId] ?? defaultChildState(childId));
          return {
            childStates: {
              ...state.childStates,
              [childId]: {
                ...existing,
                manualAttendance: existing.manualAttendance.filter(e => e.id !== entryId),
              },
            },
          };
        });
      },

      resetChildCompliance: (childId) => {
        set(state => ({
          childStates: {
            ...state.childStates,
            [childId]: defaultChildState(childId),
          },
        }));
      },
    }),
    { name: 'homeschool-compliance', version: 0, migrate: (state) => state }
  )
);
